import { useState } from "react";
import { supabase } from "@/lib/supabase";
import PhotoPositionSelector from "@/components/base/PhotoPositionSelector";
import MemberPhotoPlaceholder from "@/components/base/MemberPhotoPlaceholder";
import { normalizePhotoPosition, PHOTO_POSITION_CLASS } from "@/lib/avatar";
import PhoneInput from "@/components/base/PhoneInput";
import { formatPhone } from "@/lib/phone";

interface Props {
  memberId: number;
  memberName: string;
  approvalToken: string;
  initialData?: MemberDetails;
  onComplete: () => void;
}

interface MemberDetails {
  full_name: string;
  profession: string;
  city_state: string;
  description: string;
  whatsapp: string;
  country_code: string;
  email: string;
  bio_long: string;
  skills: string;
  languages: string;
  education: string;
  services: string;
  linkedin: string;
  facebook: string;
  tiktok: string;
  instagram: string;
  website: string;
  experience_years: string;
  photo: string;
  photo_position: string;
  has_physical_business: boolean;
  business_name: string;
  business_address: string;
  business_city: string;
  business_state: string;
  business_zip: string;
}

function normalizeUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

const EMPTY_DETAILS: MemberDetails = {
  full_name: "",
  profession: "",
  city_state: "",
  description: "",
  whatsapp: "",
  country_code: "+1",
  email: "",
  bio_long: "",
  skills: "",
  languages: "",
  education: "",
  services: "",
  linkedin: "",
  facebook: "",
  tiktok: "",
  instagram: "",
  website: "",
  experience_years: "",
  photo: "",
  photo_position: "top",
  has_physical_business: false,
  business_name: "",
  business_address: "",
  business_city: "",
  business_state: "",
  business_zip: "",
};

function saveToken(memberId: number, token: string) {
  try {
    const raw = localStorage.getItem("entreelas_tokens") || "";
    const tokens = JSON.parse(raw);
    tokens[String(memberId)] = token;
    localStorage.setItem("entreelas_tokens", JSON.stringify(tokens));
  } catch {
    // localStorage indisponível — segue o baile
  }
}

export default function CompleteForm({ memberId, memberName, approvalToken, initialData, onComplete }: Props) {
  const [formData, setFormData] = useState<MemberDetails>(() => {
    const base = initialData || { ...EMPTY_DETAILS };
    return {
      ...base,
      country_code: base.country_code || "+1",
      whatsapp: formatPhone(base.whatsapp || "", base.country_code || "+1"),
    };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isEditing = !!initialData;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    const { error: updateError } = await supabase.rpc("registration_profile_save", {
      p_token: approvalToken,
      p_data: {
        full_name: formData.full_name.trim(),
        profession: formData.profession.trim(),
        city_state: formData.city_state.trim(),
        description: formData.description.trim(),
        whatsapp: formData.whatsapp.replace(/\D/g, ""),
        country_code: formData.country_code,
        email: formData.email.trim(),
        bio_long: formData.bio_long.trim(),
        skills: formData.skills.trim(),
        languages: formData.languages.trim(),
        education: formData.education.trim(),
        services: formData.services.trim(),
        linkedin: normalizeUrl(formData.linkedin),
        facebook: normalizeUrl(formData.facebook),
        tiktok: formData.tiktok.trim(),
        instagram: formData.instagram.trim(),
        website: normalizeUrl(formData.website),
        experience_years: formData.experience_years.trim(),
        photo_position: formData.photo_position || "top",
        has_physical_business: formData.has_physical_business,
        business_name: formData.has_physical_business ? formData.business_name.trim() || null : null,
        business_address: formData.has_physical_business ? formData.business_address.trim() || null : null,
        business_city: formData.has_physical_business ? formData.business_city.trim() || null : null,
        business_state: formData.has_physical_business ? formData.business_state.trim() || null : null,
        business_zip: formData.has_physical_business ? formData.business_zip.trim() || null : null,
      },
    });

    if (updateError) {
      console.error("Erro ao atualizar perfil:", updateError);
      setError("Erro ao salvar. Tente novamente.");
      setSaving(false);
      return;
    }

    // Salva o token no navegador pra futuras edições
    saveToken(memberId, approvalToken);

    setSaving(false);
    onComplete();
  };

  return (
    <div className="w-full px-6 md:px-10 lg:px-16 max-w-2xl mx-auto">
      <div className="text-center mb-10 md:mb-14">
        <p className="font-body text-xs tracking-[0.25em] uppercase text-primary mb-3">
          {isEditing ? "Editar perfil" : "Complete seu perfil"}
        </p>
        <h2 className="font-display text-2xl md:text-3xl text-brown mb-3">
          {isEditing ? `Editando, ${memberName}!` : `Bem-vinda, ${memberName}!`}
        </h2>
        <p className="font-body text-brown/70 text-base leading-relaxed max-w-xl mx-auto">
          {isEditing
            ? "Atualize as informações do seu perfil. As mudanças serão salvas automaticamente na comunidade."
            : <>Seu cadastro foi <strong className="text-primary">aprovado</strong>! Agora é só preencher
              os detalhes abaixo para seu perfil ficar completo na comunidade.</>
          }
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 md:p-10 space-y-5">

        {/* Nome */}
        <div>
          <label className="font-body text-sm text-brown block mb-2">
            Nome completo <span className="text-primary">*</span>
          </label>
          <input
            type="text" name="full_name" required
            value={formData.full_name} onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
            placeholder="Seu nome completo"
          />
        </div>

        {/* Profissão + Cidade */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Profissão <span className="text-primary">*</span>
            </label>
            <input
              type="text" name="profession" required
              value={formData.profession} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="Ex: Advogada, Designer, Coach..."
            />
          </div>
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Cidade / Estado <span className="text-primary">*</span>
            </label>
            <input
              type="text" name="city_state" required
              value={formData.city_state} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="Ex: São Paulo, SP"
            />
          </div>
        </div>

        {/* Descrição curta */}
        <div>
          <label className="font-body text-sm text-brown block mb-2">
            Descrição curta <span className="text-primary">*</span>
          </label>
          <textarea
            name="description" required maxLength={300}
            value={formData.description} onChange={handleChange}
            rows={3}
            className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors resize-none"
            placeholder="Uma breve frase sobre você e o que faz..."
          />
          <p className="font-body text-xs text-brown/50 mt-1 text-right">{formData.description.length}/300</p>
        </div>

        {/* WhatsApp */}
        <div>
          <label className="font-body text-sm text-brown block mb-2">
            WhatsApp <span className="text-primary">*</span>
          </label>
          <PhoneInput
            countryCode={formData.country_code}
            whatsapp={formData.whatsapp}
            onChange={({ countryCode, whatsapp }) =>
              setFormData({ ...formData, country_code: countryCode, whatsapp })
            }
            required
            disabled={saving}
          />
        </div>

        {/* Email */}
        <div>
          <label className="font-body text-sm text-brown block mb-2">
            Email <span className="text-primary">*</span>
          </label>
          <input
            type="email" name="email" required
            value={formData.email} onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
            placeholder="seu@email.com"
          />
          {isEditing && (
            <p className="font-body text-xs text-brown/40 mt-1">
              Alterar o email aqui atualiza apenas o contato no perfil. Se precisar mudar o email de login, entre em contato com a administradora.
            </p>
          )}
        </div>

        <div className="border-t border-nude pt-4" />

        {/* Bio completa */}
        <div>
          <label className="font-body text-sm text-brown block mb-2">
            Bio completa <span className="text-primary">*</span>
          </label>
          <textarea
            name="bio_long" required maxLength={1000}
            value={formData.bio_long} onChange={handleChange}
            rows={5}
            className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors resize-none"
            placeholder="Conte sua história, sua trajetória, o que te inspira e o que você busca..."
          />
          <p className="font-body text-xs text-brown/50 mt-1 text-right">{formData.bio_long.length}/1000</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Habilidades / Especialidades
            </label>
            <input
              type="text" name="skills"
              value={formData.skills} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="Marketing, Coaching, Design..."
            />
            <p className="font-body text-xs text-brown/40 mt-1">Separe por vírgulas</p>
          </div>
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Idiomas
            </label>
            <input
              type="text" name="languages"
              value={formData.languages} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="Português, Inglês, Espanhol..."
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Formação / Certificações
            </label>
            <input
              type="text" name="education"
              value={formData.education} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="Graduação, pós, certificações..."
            />
          </div>
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Anos de experiência
            </label>
            <input
              type="text" name="experience_years"
              value={formData.experience_years} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="5+ anos"
            />
          </div>
        </div>

        <div>
          <label className="font-body text-sm text-brown block mb-2">
            Serviços que oferece
          </label>
          <textarea
            name="services" maxLength={500}
            value={formData.services} onChange={handleChange}
            rows={3}
            className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors resize-none"
            placeholder="Descreva os serviços que você oferece..."
          />
          <p className="font-body text-xs text-brown/50 mt-1 text-right">{formData.services.length}/500</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              LinkedIn
            </label>
            <input
              type="text" name="linkedin"
              value={formData.linkedin} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="linkedin.com/in/seuperfil"
            />
          </div>
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Facebook
            </label>
            <input
              type="text" name="facebook"
              value={formData.facebook} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="facebook.com/seuperfil"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              Instagram
            </label>
            <input
              type="text" name="instagram"
              value={formData.instagram} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="@seuinstagram"
            />
          </div>
          <div>
            <label className="font-body text-sm text-brown block mb-2">
              TikTok
            </label>
            <input
              type="text" name="tiktok"
              value={formData.tiktok} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
              placeholder="@seutiktok"
            />
          </div>
        </div>

        <div>
          <label className="font-body text-sm text-brown block mb-2">
            Website / Portfólio
          </label>
          <input
            type="text" name="website"
            value={formData.website} onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
            placeholder="seusite.com"
          />
        </div>

        <div>
          <span className="font-body text-sm text-brown block mb-2">
            Enquadramento da foto do perfil
          </span>
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
            {/* Prévia redonda — mesmo formato que aparece no site */}
            <div className="shrink-0">
              {formData.photo ? (
                <div className="w-24 h-24 rounded-full overflow-hidden border border-nude">
                  <img
                    src={formData.photo}
                    alt="Prévia da foto do perfil"
                    className={`w-full h-full object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(formData.photo_position)]}`}
                  />
                </div>
              ) : (
                <MemberPhotoPlaceholder size="lg" rounded="full" />
              )}
            </div>
            <div className="flex-1 w-full">
              <PhotoPositionSelector
                value={formData.photo_position}
                onChange={(v) => setFormData({ ...formData, photo_position: v })}
                disabled={saving}
              />
              <p className="font-body text-xs text-brown/40 mt-2">
                Se a sua foto aparecer cortada, escolha qual parte deve ficar visível: topo, centro ou base.
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-nude pt-4" />

        {/* ─── Negócio com endereço físico ─── */}
        <div>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={formData.has_physical_business}
              onChange={(e) => setFormData({ ...formData, has_physical_business: e.target.checked })}
              className="mt-0.5 w-4 h-4 accent-primary cursor-pointer"
            />
            <span className="font-body text-sm text-brown">
              Tenho um negócio com endereço físico
            </span>
          </label>

          {formData.has_physical_business && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="font-body text-sm text-brown block mb-2">
                  Nome do negócio
                </label>
                <input
                  type="text" name="business_name"
                  value={formData.business_name} onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                  placeholder="Nome do seu negócio"
                />
              </div>
              <div>
                <label className="font-body text-sm text-brown block mb-2">
                  Endereço
                </label>
                <input
                  type="text" name="business_address"
                  value={formData.business_address} onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                  placeholder="Rua, número, complemento"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div>
                  <label className="font-body text-sm text-brown block mb-2">Cidade</label>
                  <input
                    type="text" name="business_city"
                    value={formData.business_city} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                    placeholder="Cidade"
                  />
                </div>
                <div>
                  <label className="font-body text-sm text-brown block mb-2">Estado</label>
                  <input
                    type="text" name="business_state"
                    value={formData.business_state} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                    placeholder="FL"
                  />
                </div>
                <div>
                  <label className="font-body text-sm text-brown block mb-2">ZIP</label>
                  <input
                    type="text" name="business_zip"
                    value={formData.business_zip} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                    placeholder="CEP / ZIP"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 font-body text-sm">
            {error}
          </div>
        )}

        <div className="pt-4">
          <button
            type="submit"
            disabled={saving}
            className="w-full font-body text-sm px-10 py-4 rounded-full bg-primary text-white tracking-wide uppercase transition-all duration-300 hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin" />
                Salvando...
              </span>
            ) : isEditing ? (
              "Salvar alterações"
            ) : (
              "Finalizar perfil"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}